import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { theme } from '../../../../core/design-system/theme'
import { useBookingContext, BookingStepShell } from '../BookingFlowView'

const DURATION_OPTIONS = [1, 1.5, 2, 2.5, 3, 4, 5, 6]

const prefersReducedMotion =
  typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false

const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: theme.colors.textSecondary,
  display: 'block',
  marginBottom: 5,
  fontFamily: 'var(--font-body)',
  letterSpacing: '0.04em',
}

const baseInputStyle = {
  background: theme.colors.card,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: theme.layout.inputRadius,
  padding: 12,
  fontSize: 15,
  color: theme.colors.textPrimary,
  fontFamily: 'var(--font-body)',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
}

export default function BookingStep3View() {
  const { viewModel } = useBookingContext()
  const navigate = useNavigate()

  const initial = viewModel.draft.schedule ?? {}
  const [date, setDate] = useState(
    initial.scheduledStart
      ? new Date(initial.scheduledStart).toISOString().slice(0, 10)
      : '',
  )
  const [time, setTime] = useState(
    initial.scheduledStart
      ? new Date(initial.scheduledStart).toTimeString().slice(0, 5)
      : '',
  )
  const [duration, setDuration] = useState(initial.durationHours ?? 1)
  const [location, setLocation] = useState(initial.locationLabel ?? '')
  const [focusedField, setFocused] = useState(null)

  const canContinue = date !== '' && time !== '' && location.trim().length > 0

  function handleContinue() {
    if (!canContinue) return
    const start = new Date(`${date}T${time}`)
    const end = new Date(start.getTime() + duration * 60 * 60 * 1000)
    viewModel.setSchedule({
      scheduledStart: start.toISOString(),
      scheduledEnd: end.toISOString(),
      durationHours: duration,
      locationLabel: location.trim(),
    })
    navigate('/book/select')
  }

  function inputStyle(field) {
    return {
      ...baseInputStyle,
      outline: focusedField === field ? `2px solid ${theme.colors.terracotta}` : 'none',
    }
  }

  return (
    <BookingStepShell
      step={3}
      title="When and where"
      onBack={() => navigate(-1)}
      onContinue={handleContinue}
      continueDisabled={!canContinue}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Date + Time */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label htmlFor="date" style={labelStyle}>
              DATE
            </label>
            <input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={inputStyle('date')}
              onFocus={() => setFocused('date')}
              onBlur={() => setFocused(null)}
            />
          </div>
          <div>
            <label htmlFor="time" style={labelStyle}>
              TIME
            </label>
            <input
              id="time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              style={inputStyle('time')}
              onFocus={() => setFocused('time')}
              onBlur={() => setFocused(null)}
            />
          </div>
        </div>

        {/* Duration pills */}
        <div>
          <span style={labelStyle}>DURATION</span>
          <div
            role="group"
            aria-label="Select duration"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}
          >
            {DURATION_OPTIONS.map((opt) => {
              const isActive = duration === opt
              return (
                <button
                  key={opt}
                  onClick={() => setDuration(opt)}
                  aria-pressed={isActive}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 99,
                    border: `1.5px solid ${isActive ? theme.colors.terracotta : theme.colors.border}`,
                    background: isActive ? theme.colors.terracotta : theme.colors.card,
                    color: isActive ? '#fff' : theme.colors.textPrimary,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: 'var(--font-body)',
                    cursor: 'pointer',
                    transition: prefersReducedMotion ? 'none' : 'all 0.15s ease',
                  }}
                >
                  {opt === 1 ? '1 hr' : `${opt} hrs`}
                </button>
              )
            })}
          </div>
        </div>

        {/* Location */}
        <div>
          <label htmlFor="location" style={labelStyle}>
            LOCATION
          </label>
          <input
            id="location"
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Restaurant name or address"
            style={inputStyle('location')}
            onFocus={() => setFocused('location')}
            onBlur={() => setFocused(null)}
          />
        </div>
      </div>
    </BookingStepShell>
  )
}
